import api from './axios';

// Backend response wrapper interface
interface BackendResponse<T> {
  data: T;
  message: string;
  success: boolean;
  timestamp: string;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'email' | 'sms' | 'push';
  status: 'pending' | 'sent' | 'failed' | 'read';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  recipientEmail?: string;
  recipientPhone?: string;
  templateId?: string;
  templateData?: Record<string, any>;
  scheduledAt?: string;
  sentAt?: string;
  readAt?: string;
  errorMessage?: string;
  retryCount: number;
  maxRetries: number;
  tenantId: string;
  userId?: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNotificationDto {
  title: string;
  message: string;
  type: 'email' | 'sms' | 'push';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  recipientEmail?: string;
  recipientPhone?: string;
  templateId?: string;
  templateData?: Record<string, any>;
  scheduledAt?: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
  userId?: string;
}

export interface UpdateNotificationDto {
  title?: string;
  message?: string;
  status?: 'pending' | 'sent' | 'failed' | 'read';
  readAt?: string;
}

export interface NotificationQueryDto {
  type?: 'email' | 'sms' | 'push';
  status?: 'pending' | 'sent' | 'failed' | 'read';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  page?: number;
  limit?: number;
}

export interface NotificationResponse {
  notifications: Notification[];
  total: number;
}

// Type guard to check if an object is a Notification
function isNotification(obj: any): obj is Notification {
  return obj && typeof obj === 'object' && 'id' in obj && 'title' in obj && 'message' in obj;
}

// Type guard to check if an object is a NotificationResponse
function isNotificationResponse(obj: any): obj is NotificationResponse {
  return obj && typeof obj === 'object' && 'notifications' in obj && 'total' in obj;
}

// Type guard to check if an object is a Notification array
function isNotificationArray(obj: any): obj is Notification[] {
  return Array.isArray(obj) && obj.every(item => isNotification(item));
}

// Fetch all notifications with pagination and filters
export const fetchNotifications = async (query?: NotificationQueryDto): Promise<NotificationResponse> => {
  try {
    const response = await api.get<BackendResponse<NotificationResponse>>('/notifications', { params: query });
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a NotificationResponse object
    if (isNotificationResponse(responseData)) {
      return responseData;
    }
    
    console.error('Invalid notifications response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

// Fetch a single notification by ID
export const fetchNotification = async (id: string): Promise<Notification> => {
  try {
    const response = await api.get<BackendResponse<Notification>>(`/notifications/${id}`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Notification object
    if (isNotification(responseData)) {
      return responseData;
    }
    
    console.error('Invalid notification response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching notification:', error);
    throw error;
  }
};

// Create a new notification
export const createNotification = async (data: CreateNotificationDto): Promise<Notification> => {
  try {
    const response = await api.post('/notifications', data);
    return response.data;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

// Update a notification
export const updateNotification = async (id: string, data: UpdateNotificationDto): Promise<Notification> => {
  try {
    const response = await api.put(`/notifications/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating notification:', error);
    throw error;
  }
};

// Mark a notification as read
export const markNotificationAsRead = async (id: string): Promise<Notification> => {
  try {
    const response = await api.put(`/notifications/${id}/read`);
    return response.data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async (): Promise<{ message: string }> => {
  try {
    const response = await api.put('/notifications/mark-all-read');
    return response.data;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

// Delete a notification
export const deleteNotification = async (id: string): Promise<{ message: string }> => {
  try {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

// Get unread notification count
export const getUnreadCount = async (): Promise<{ count: number }> => {
  try {
    const response = await api.get<BackendResponse<{ count: number }>>('/notifications/unread-count');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a count object
    if (responseData && typeof responseData === 'object' && 'count' in responseData && typeof responseData.count === 'number') {
      return { count: responseData.count };
    }
    
    console.error('Invalid unread count response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching unread count:', error);
    throw error;
  }
};

// Template-based notification endpoints
export const createWaterQualityAlert = async (data: {
  pondId: string;
  pondName: string;
  issue: string;
}): Promise<Notification> => {
  try {
    const response = await api.post('/notifications/water-quality-alert', data);
    return response.data;
  } catch (error) {
    console.error('Error creating water quality alert:', error);
    throw error;
  }
};

export const createMortalityAlert = async (data: {
  animalGroupId: string;
  species: string;
  mortalityRate: number;
}): Promise<Notification> => {
  try {
    const response = await api.post('/notifications/mortality-alert', data);
    return response.data;
  } catch (error) {
    console.error('Error creating mortality alert:', error);
    throw error;
  }
};

export const createFeedStockAlert = async (data: {
  feedId: string;
  feedName: string;
  currentStock: number;
  threshold: number;
}): Promise<Notification> => {
  try {
    const response = await api.post('/notifications/feed-stock-alert', data);
    return response.data;
  } catch (error) {
    console.error('Error creating feed stock alert:', error);
    throw error;
  }
};

export const createMaintenanceReminder = async (data: {
  pondId: string;
  pondName: string;
  maintenanceType: string;
  dueDate: string;
}): Promise<Notification> => {
  try {
    const response = await api.post('/notifications/maintenance-reminder', data);
    return response.data;
  } catch (error) {
    console.error('Error creating maintenance reminder:', error);
    throw error;
  }
};

// Test email server connection
export const testEmailConnection = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/notifications/test-connection');
    return response.data;
  } catch (error) {
    console.error('Error testing email connection:', error);
    throw error;
  }
};

// Send a test email
export const sendTestEmail = async (email: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/notifications/test-email', { email });
    return response.data;
  } catch (error) {
    console.error('Error sending test email:', error);
    throw error;
  }
};

export const notificationApi = {
  fetchNotifications,
  fetchNotification,
  createNotification,
  updateNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  getUnreadCount,
  createWaterQualityAlert,
  createMortalityAlert,
  createFeedStockAlert,
  createMaintenanceReminder,
  testEmailConnection,
  sendTestEmail,
}; 